import CT from '@marketplace-ai/tags';
import { FilterWrapperParams, IAfterGuiAttachedParams } from 'ag-grid-community';
import { useGridFilterDisplay, type CustomFilterDisplayProps } from 'ag-grid-react';
import { useCallback, useEffect, useRef, useState } from 'react';

export const CustomDateRangeFilter = ({
  model,
  onModelChange,
}: CustomFilterDisplayProps & FilterWrapperParams) => {
  const refInput = useRef<HTMLInputElement>(null);
  const afterGuiAttached = useCallback((params?: IAfterGuiAttachedParams) => {
    if (!params || !params.suppressFocus) {
      refInput.current?.focus();
    }
  }, []);

  useGridFilterDisplay({
    afterGuiAttached,
  });

  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const clearDates = () => {
    setStartDate('');
    setEndDate('');
  };

  useEffect(() => {
    // Match CustomSetFilter: emit null for the inactive state so ag-grid
    // treats a fully-empty range as "no filter" rather than a placeholder model.
    if (!startDate && !endDate) {
      onModelChange(null);
      return;
    }
    onModelChange({ startDate: startDate || null, endDate: endDate || null });
  }, [startDate, endDate, onModelChange]);

  // Sync local inputs when the grid clears this filter externally (e.g., column
  // menu "Reset", grid API setFilterModel(null), or floating filter clear).
  useEffect(() => {
    if (model == null) {
      setStartDate('');
      setEndDate('');
    }
  }, [model]);

  return (
    <div className="p-3 w-52 flex flex-col gap-2.5">
      <div>
        <label
          htmlFor="ag-date-filter-from"
          className="text-xs font-medium text-grey-300 block mb-1"
        >
          From:
        </label>
        <input
          id="ag-date-filter-from"
          data-cy={CT.agGrid.filterFromDate}
          ref={refInput}
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          className="w-full px-2.5 py-1.5 border border-grey-800 rounded-md text-sm bg-white transition-colors duration-150 focus:border-primary-50 focus:ring-3 focus:ring-primary-50/12 focus:outline-none"
        />
      </div>

      <div>
        <label htmlFor="ag-date-filter-to" className="text-xs font-medium text-grey-300 block mb-1">
          To:
        </label>
        <input
          id="ag-date-filter-to"
          type="date"
          data-cy={CT.agGrid.filterToDate}
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          className="w-full px-2.5 py-1.5 border border-grey-800 rounded-md text-sm bg-white transition-colors duration-150 focus:border-primary-50 focus:ring-3 focus:ring-primary-50/12 focus:outline-none"
        />
      </div>

      <button
        type="button"
        onClick={clearDates}
        className="text-xs font-medium text-primary-50 hover:text-primary-t-300 transition-colors mt-2"
      >
        Clear
      </button>
    </div>
  );
};
